import { useEffect, useState } from 'react';
import { Button, Checkbox, ConfigProvider, Drawer, Form, InputNumber, Select, Switch } from 'antd';
import heIL from 'antd/locale/he_IL';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ANY_HUG,
  DEFAULT_STAFF_PROFILE,
  PNL_UNIT_NONE,
  StaffSalarySettingsSchema,
  TAX_TYPE_LABELS,
  defaultUnitId,
  enabledMechanisms,
  formatWithholdingPct,
  type BoxMechanism,
  type ClassBonusRule,
  type ComponentSource,
  type GlobalSalary,
  type HugimPercentRow,
  type HugRole,
  rowRole,
  type SalaryComponents,
  type StaffProfile,
  type StaffSalarySettings,
  type TravelKmLocation,
} from '@salaries/shared';
import { useAuth } from '../../auth/AuthContext';
import {
  getStaffMechanisms,
  getStaffSalarySettings,
  updateStaffMechanisms,
  updateStaffSalarySettings,
} from '../../api/salarySettings';
import { getBoxSalaryDefaults } from '../../api/salaryDefaults';
import { getStaffProfile, updateStaffProfile } from '../../api/staffProfile';
import { getPnlUnits } from '../../api/pnlUnits';
import {
  deleteHugimAddonPercent,
  deleteHugimPercent,
  getHugimAddonSettings,
  getHugimSettings,
  upsertHugimAddonPercent,
  upsertHugimPercent,
} from '../../api/hugim';
import { Loader } from '../../components/Loader';
import { CategoryRateFields } from './CategoryRateFields';
import { SeriesRateFields } from './SeriesRateFields';
import { ClassBonusFields } from './ClassBonusFields';
import { TravelKmLocationsFields } from './TravelKmLocationsFields';
import { CoachHugPercentFields } from './CoachHugPercentFields';
import { ManualAdjustmentsFields } from './ManualAdjustmentsFields';
import { byCourse, type CourseRolePercent } from '../settings/CoursePercentList';
import { PercentSumWarning } from '../settings/PercentSumWarning';

/** A staff member's own all-courses percentage, per role; null = inherit. */
interface RolePercents {
  primary: number | null;
  secondary: number | null;
}
const NO_ROLE_PERCENTS: RolePercents = { primary: null, secondary: null };
import { StaffTaxBankFields } from '../settings/StaffTaxBankFields';
import { VolumeBonusFields } from '../settings/VolumeBonusFields';
import { DailyClassAdjustmentFields } from '../settings/DailyClassAdjustmentFields';
import { ConversionTiersField } from '../settings/ConversionBonusFields';
import { SettingCard, SettingsAccordion } from '../../components/SettingCard';
import {
  BaseFields,
  DEFAULT_COMPONENT_FLAT,
  EmptyClassPayFields,
  TravelFields,
  WeekendFields,
  componentsToFlat,
  flatToComponents,
  type ComponentFlat,
} from '../settings/salaryComponentFields';

interface Props {
  open: boolean;
  staffId: number | null;
  staffName: string;
  onClose: () => void;
  onSaved: () => void;
}

// The form combines the 3-state source selects with the shared component fields
// (only shown for a 'custom' source). Base / weekend / travel only.
interface StaffFlat extends ComponentFlat {
  base_source: ComponentSource;
  weekend_source: ComponentSource;
  travel_source: ComponentSource;
  empty_class_pay_source: ComponentSource;
  volume_bonus_source: ComponentSource;
  daily_class_adjustment_source: ComponentSource;
  category_rates_source: ComponentSource;
  series_rates_source: ComponentSource;
  // Only the ₪ amount is overridable per coach (enabled + window are global).
  conversion_source: ComponentSource;
}

const SOURCE_OPTIONS = [
  { value: 'default', label: 'ברירת מחדל של העסק' },
  { value: 'none', label: 'ללא (לא רלוונטי לאיש צוות זה)' },
  { value: 'custom', label: 'מותאם אישית' },
] as const;

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-heading-3-bold text-main-text mb-3 mt-2">{children}</div>;
}

const MECHANISM_LABELS: Record<BoxMechanism, string> = {
  group_classes: 'שיעורים (קבוצתי)',
  hugim: 'חוגים (אחוז מהתשלום)',
  time_clock: 'שעון נוכחות',
};

// Build the initial flat values: custom fields are seeded from the staff's own
// custom value where present, else from the box defaults — so switching a
// component to "מותאם אישית" starts from the business default (per spec).
function buildInitialFlat(staff: StaffSalarySettings, defaults: SalaryComponents): StaffFlat {
  const seedComponents: SalaryComponents = {
    base_salary:
      staff.base_salary.source === 'custom' ? staff.base_salary.value : defaults.base_salary,
    weekend_bonus:
      staff.weekend_bonus.source === 'custom' ? staff.weekend_bonus.value : defaults.weekend_bonus,
    travel: staff.travel.source === 'custom' ? staff.travel.value : defaults.travel,
    empty_class_pay:
      staff.empty_class_pay.source === 'custom'
        ? staff.empty_class_pay.value
        : defaults.empty_class_pay,
    volume_bonus:
      staff.volume_bonus?.source === 'custom'
        ? staff.volume_bonus.value
        : (defaults.volume_bonus ?? { enabled: false, tiers: [] }),
    daily_class_adjustment:
      staff.daily_class_adjustment?.source === 'custom'
        ? staff.daily_class_adjustment.value
        : (defaults.daily_class_adjustment ?? { enabled: false, tiers: [] }),
    // Box policy, never per-coach — carried through so the shape stays whole.
    cancelled_class_pay: defaults.cancelled_class_pay,
    // Seed a custom list from the coach's own, else a copy of the box's.
    series_rates:
      staff.series_rates?.source === 'custom'
        ? staff.series_rates.value
        : (defaults.series_rates ?? []),
    category_rates:
      staff.category_rates?.source === 'custom'
        ? staff.category_rates.value
        : (defaults.category_rates ?? []),
    conversion_bonus: {
      enabled: defaults.conversion_bonus.enabled,
      window_days: defaults.conversion_bonus.window_days,
      // Seed the custom ladder from the staff's own value, else the box default.
      tiers:
        staff.conversion_bonus_tiers.source === 'custom'
          ? staff.conversion_bonus_tiers.value
          : (defaults.conversion_bonus.tiers ?? []),
    },
  };
  return {
    ...componentsToFlat(seedComponents),
    base_source: staff.base_salary.source,
    weekend_source: staff.weekend_bonus.source,
    travel_source: staff.travel.source,
    empty_class_pay_source: staff.empty_class_pay.source,
    volume_bonus_source: staff.volume_bonus?.source ?? 'default',
    daily_class_adjustment_source: staff.daily_class_adjustment?.source ?? 'default',
    category_rates_source: staff.category_rates?.source ?? 'default',
    series_rates_source: staff.series_rates?.source ?? 'default',
    conversion_source: staff.conversion_bonus_tiers.source,
  };
}

export function StaffSalaryEditDrawer({ open, staffId, staffName, onClose, onSaved }: Props) {
  const [form] = Form.useForm<StaffFlat>();
  const queryClient = useQueryClient();
  const authSettings = useAuth().settings;
  const authUserId = useAuth().user?.arbox_users_boxes_id;
  // The box's ENABLED mechanisms (what the staff multi-select may choose
  // from) — a box can run any combination now, see BoxSettingsDrawer.
  const boxMechanisms = authSettings ? enabledMechanisms(authSettings) : (['group_classes'] as BoxMechanism[]);

  // This staff member's OWN assigned mechanisms — additive: a staff assigned
  // e.g. ['hugim', 'time_clock'] gets both sections below, consolidated into
  // one salary line server-side (see payroll-mechanisms.service.ts).
  const [staffMechanisms, setStaffMechanisms] = useState<BoxMechanism[]>([]);
  const hasGroupClasses = staffMechanisms.includes('group_classes');
  const hasHugim = staffMechanisms.includes('hugim');
  const hasTimeClock = staffMechanisms.includes('time_clock');
  // Any mechanism that uses the generic base/travel components.
  const hasGenericPay = hasGroupClasses || hasTimeClock;

  const [profile, setProfile] = useState<StaffProfile>(DEFAULT_STAFF_PROFILE);
  // Per-staff hugim % override, one per role (null = inherit the per-hug /
  // global default of that role).
  const [hugimPercent, setHugimPercent] = useState<RolePercents>(NO_ROLE_PERCENTS);
  // This staff member's per-hug overrides — the (staff, hug) rows, both roles.
  // Staged; saved as a diff against what was loaded.
  const [coachHugPercents, setCoachHugPercents] = useState<CourseRolePercent[]>([]);
  // The same two levels for מוצרים נלווים (add-ons). Staged the same way.
  const [addonsPercent, setAddonsPercent] = useState<RolePercents>(NO_ROLE_PERCENTS);
  const [coachAddonPercents, setCoachAddonPercents] = useState<CourseRolePercent[]>([]);
  // Per-class (name + location) fixed ₪ supplements (group-classes only).
  const [classBonuses, setClassBonuses] = useState<ClassBonusRule[]>([]);
  // Per-location one-way distances for the per_km travel mode (personal per staff).
  const [travelKmLocations, setTravelKmLocations] = useState<TravelKmLocation[]>([]);
  // Manually-assigned P&L unit — only for a staff member with NO group_classes
  // assignment. A coach teaching group classes has their unit auto-derived
  // from where they actually taught (pickCoachPrimaryUnit), and that wins over
  // any manual value in resolveUnit — so offering the picker alongside
  // group_classes would show a control that silently does nothing. Shown in
  // ONE shared control for hugim/time_clock so the owner never sets it more
  // than once. null = box default/primary unit.
  // שכר גלובלי — a fixed ₪ amount every pay period, no matter what. When ON
  // it REPLACES every mechanism for this staff member, so the mechanism and
  // pay-component sections are hidden (their stored values are kept as-is).
  const [globalSalary, setGlobalSalary] = useState<GlobalSalary>({ enabled: false, amount: 0 });
  const globalOn = globalSalary.enabled;
  // A global employee has no activity to derive a unit from either.
  const needsManualUnit = globalOn || ((hasHugim || hasTimeClock) && !hasGroupClasses);
  const [pnlUnitId, setPnlUnitId] = useState<number | null>(null);

  const pnlUnitsQuery = useQuery({
    queryKey: ['pnl-units'],
    queryFn: getPnlUnits,
    enabled: open && needsManualUnit,
  });

  const hugimQuery = useQuery({
    queryKey: ['hugim-percentages'],
    queryFn: getHugimSettings,
    enabled: open && hasHugim,
  });
  const addonsQuery = useQuery({
    queryKey: ['hugim-addon-percentages'],
    queryFn: getHugimAddonSettings,
    enabled: open && hasHugim,
  });

  const mechanismsQuery = useQuery({
    queryKey: ['staff-mechanisms', staffId],
    queryFn: () => getStaffMechanisms(staffId!),
    enabled: open && staffId != null,
  });

  const settingsQuery = useQuery({
    queryKey: ['salary-settings', staffId],
    queryFn: () => getStaffSalarySettings(staffId!),
    enabled: open && staffId != null,
  });
  const defaultsQuery = useQuery({
    queryKey: ['salary-defaults'],
    queryFn: getBoxSalaryDefaults,
    enabled: open,
  });
  const profileQuery = useQuery({
    queryKey: ['staff-profile', staffId],
    queryFn: () => getStaffProfile(staffId!),
    enabled: open && staffId != null,
  });

  // Only the box's enabled mechanisms have a checkbox, so a stored one the box
  // has since turned off would ride along invisibly and fail the whole save
  // server-side ("המנגנון … אינו פעיל בעסק") — box 25421 after switching to
  // hugim. Drop it here; the save then writes the cleaned set.
  const boxMechanismsKey = boxMechanisms.join(',');
  useEffect(() => {
    if (open && mechanismsQuery.data) {
      setStaffMechanisms(mechanismsQuery.data.filter((m) => boxMechanisms.includes(m)));
    }
    // boxMechanisms is rebuilt each render; boxMechanismsKey is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mechanismsQuery.data, boxMechanismsKey]);

  useEffect(() => {
    if (open && settingsQuery.data && defaultsQuery.data) {
      form.setFieldsValue(buildInitialFlat(settingsQuery.data, defaultsQuery.data));
      setClassBonuses(settingsQuery.data.class_bonuses ?? []);
      setTravelKmLocations(settingsQuery.data.travel_km_locations ?? []);
      setPnlUnitId(settingsQuery.data.pnl_unit_id ?? null);
      setGlobalSalary(settingsQuery.data.global_salary ?? { enabled: false, amount: 0 });
    }
  }, [open, settingsQuery.data, defaultsQuery.data, form]);

  useEffect(() => {
    if (open && profileQuery.data) {
      // Default the account-holder to the staff member's own name when unset.
      setProfile({
        ...profileQuery.data,
        bank_account_holder: profileQuery.data.bank_account_holder || staffName || null,
      });
    }
  }, [open, profileQuery.data, staffName]);

  useEffect(() => {
    if (open && hasHugim && hugimQuery.data && staffId != null) {
      const own = hugimQuery.data.overrides.filter((o) => o.coach_arbox_users_boxes_id === staffId);
      setHugimPercent(ownRolePercents(own));
      setCoachHugPercents(byCourse(own.filter((o) => o.hug_key !== ANY_HUG)));
    }
  }, [open, hasHugim, hugimQuery.data, staffId]);

  useEffect(() => {
    if (open && hasHugim && addonsQuery.data && staffId != null) {
      const own = addonsQuery.data.overrides.filter((o) => o.coach_arbox_users_boxes_id === staffId);
      setAddonsPercent(ownRolePercents(own));
      setCoachAddonPercents(byCourse(own.filter((o) => o.hug_key !== ANY_HUG)));
    }
  }, [open, hasHugim, addonsQuery.data, staffId]);

  const baseSource = Form.useWatch('base_source', form);
  const weekendSource = Form.useWatch('weekend_source', form);
  const travelSource = Form.useWatch('travel_source', form);
  const travelType = Form.useWatch('travel_type', form);
  const emptyClassPaySource = Form.useWatch('empty_class_pay_source', form);
  const volumeBonusSource = Form.useWatch('volume_bonus_source', form);
  const dailyClassAdjustmentSource = Form.useWatch('daily_class_adjustment_source', form);
  const categoryRatesSource = Form.useWatch('category_rates_source', form);
  const seriesRatesSource = Form.useWatch('series_rates_source', form);
  const conversionSource = Form.useWatch('conversion_source', form);

  // The effective travel type after the 3-state source resolves against the box
  // default — drives whether the per-location distance editor shows (distances
  // are personal and apply even when the ₪/km rate is inherited).
  const effectiveTravelType =
    travelSource === 'custom'
      ? travelType
      : travelSource === 'none'
        ? 'none'
        : defaultsQuery.data?.travel.type;
  const showKmLocations = effectiveTravelType === 'per_km';

  // Short summary line for each collapsed setting card.
  const srcLabel = (s?: ComponentSource) =>
    s === 'custom' ? 'מותאם אישית' : s === 'none' ? 'ללא' : 'ברירת מחדל של העסק';

  const mutation = useMutation({
    mutationFn: (fn: () => Promise<void>) => fn(),
    onSuccess: () => {
      toast.success('הגדרות השכר נשמרו');
      void queryClient.invalidateQueries({ queryKey: ['salary-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['staff-mechanisms'] });
      void queryClient.invalidateQueries({ queryKey: ['staff-profile', staffId] });
      void queryClient.invalidateQueries({ queryKey: ['hugim-percentages'] });
      void queryClient.invalidateQueries({ queryKey: ['hugim-addon-percentages'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
      onSaved();
      onClose();
    },
    onError: (err) => toast.error((err as Error).message || 'שגיאה בשמירת ההגדרות'),
  });

  const onFinish = (values: StaffFlat) => {
    if (staffId == null) return;

    const components = flatToComponents(values);
    const existing = settingsQuery.data;

    // Wrap each resolved component value in its 3-state override. A staff
    // member whose mechanisms don't need a given section (e.g. hugim-only
    // skips base/travel/etc. entirely) never registers that Form.Item, so
    // `values.<x>_source` is undefined there — two problems follow if left
    // unhandled: (1) the schema rejects `{source: undefined}`, silently
    // failing the WHOLE save (mechanisms, hugim %, profile, pnl_unit_id)
    // before any network call; (2) naively defaulting to 'default' would
    // WIPE a real customization from when this staff had a different
    // mechanism active (e.g. a fixed base salary set while group_classes).
    // So an unregistered field falls back to whatever was already loaded,
    // not to 'default' — only a section actually shown to the operator can
    // change.
    const settings: StaffSalarySettings = {
      arbox_users_boxes_id: staffId,
      base_salary:
        values.base_source === undefined
          ? (existing?.base_salary ?? { source: 'default' })
          : values.base_source === 'custom'
            ? { source: 'custom', value: components.base_salary }
            : { source: values.base_source },
      weekend_bonus:
        values.weekend_source === undefined
          ? (existing?.weekend_bonus ?? { source: 'default' })
          : values.weekend_source === 'custom'
            ? // No "פעיל" toggle here — choosing "מותאם אישית" implies active.
              { source: 'custom', value: { ...components.weekend_bonus, enabled: true } }
            : { source: values.weekend_source },
      travel:
        values.travel_source === undefined
          ? (existing?.travel ?? { source: 'default' })
          : values.travel_source === 'custom'
            ? { source: 'custom', value: components.travel }
            : { source: values.travel_source },
      travel_km_locations: travelKmLocations,
      empty_class_pay:
        values.empty_class_pay_source === undefined
          ? (existing?.empty_class_pay ?? { source: 'default' })
          : values.empty_class_pay_source === 'custom'
            ? // No "פעיל" toggle here — choosing "מותאם אישית" implies active.
              { source: 'custom', value: { ...components.empty_class_pay, enabled: true } }
            : { source: values.empty_class_pay_source },
      volume_bonus:
        values.volume_bonus_source === undefined
          ? (existing?.volume_bonus ?? { source: 'default' })
          : values.volume_bonus_source === 'custom'
            ? // No "פעיל" toggle here — choosing "מותאם אישית" implies active.
              { source: 'custom', value: { ...components.volume_bonus, enabled: true } }
            : { source: values.volume_bonus_source },
      daily_class_adjustment:
        values.daily_class_adjustment_source === undefined
          ? (existing?.daily_class_adjustment ?? { source: 'default' })
          : values.daily_class_adjustment_source === 'custom'
            ? // No "פעיל" toggle here — choosing "מותאם אישית" implies active.
              { source: 'custom', value: { ...components.daily_class_adjustment, enabled: true } }
            : { source: values.daily_class_adjustment_source },
      conversion_bonus_tiers:
        values.conversion_source === undefined
          ? (existing?.conversion_bonus_tiers ?? { source: 'default' })
          : values.conversion_source === 'custom'
            ? { source: 'custom', value: components.conversion_bonus.tiers }
            : { source: values.conversion_source },
      class_bonuses: classBonuses,
      // The whole list is the override — a custom list replaces the box's.
      series_rates:
        values.series_rates_source === undefined
          ? (existing?.series_rates ?? { source: 'default' })
          : values.series_rates_source === 'custom'
            ? { source: 'custom', value: components.series_rates }
            : { source: values.series_rates_source },
      category_rates:
        values.category_rates_source === undefined
          ? (existing?.category_rates ?? { source: 'default' })
          : values.category_rates_source === 'custom'
            ? { source: 'custom', value: components.category_rates }
            : { source: values.category_rates_source },
      // Ignored server-side for group_classes (unit derives from class
      // locations there) — only meaningful for hugim/time_clock, but always
      // sent so switching a staff member's mechanisms later doesn't lose it.
      pnl_unit_id: pnlUnitId,
      global_salary: globalSalary,
    };

    const parsed = StaffSalarySettingsSchema.safeParse(settings);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || 'נתונים שגויים');
      return;
    }

    // Additive: a staff member can be assigned more than one mechanism at
    // once (e.g. hugim + time_clock), consolidated server-side into one
    // salary line — so each mechanism's own settings save independently,
    // never exclusively.
    mutation.mutate(async () => {
      await updateStaffMechanisms(staffId, staffMechanisms);
      if (hasHugim) {
        await saveCoachPercentLevels(staffId, hugimQuery.data?.overrides ?? [], hugimPercent, coachHugPercents, {
          upsert: upsertHugimPercent,
          remove: deleteHugimPercent,
        });
        await saveCoachPercentLevels(
          staffId,
          addonsQuery.data?.overrides ?? [],
          addonsPercent,
          coachAddonPercents,
          { upsert: upsertHugimAddonPercent, remove: deleteHugimAddonPercent },
        );
      }
      // hasGenericPay alone would skip hugim-only staff — but pnl_unit_id (see
      // above) lives on this same settings row and applies to hugim too, so a
      // hugim-only staff member must still get this call to save their unit.
      // global_salary lives on this row too — a global employee usually has
      // no mechanism at all, so it must be saved whenever it is (or was) on.
      if (hasGenericPay || hasHugim || globalOn || existing?.global_salary?.enabled) {
        await updateStaffSalarySettings(staffId, parsed.data);
      }
      await updateStaffProfile(staffId, profile);
    });
  };

  const loading =
    settingsQuery.isLoading ||
    defaultsQuery.isLoading ||
    profileQuery.isLoading ||
    mechanismsQuery.isLoading;

  return (
    <Drawer
      // Arbox sidebar header title is text-heading-1 (20/600), same as modals
      // and the Arbox parent app — matches the expenses add-on.
      title={
        <span className="text-main-text" style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>
          הגדרות שכר מתקדמות - {staffName}
        </span>
      }
      placement="left"
      open={open}
      onClose={onClose}
      width={560}
      closable={false}
      extra={
        <button
          type="button"
          aria-label="סגור"
          onClick={onClose}
          className="flex items-center justify-center w-8 h-8 rounded text-secondary-text hover:bg-grey-bg hover:text-main-text text-2xl leading-none"
        >
          ×
        </button>
      }
      // Arbox sidebar: no divider under the title, no border above the footer.
      // Footer lives in the antd `footer` prop so it stays sticky while the body
      // scrolls between the (sticky) header and (sticky) footer. Tight header +
      // small body top-padding so the scrollable content sits close to the title.
      styles={{
        header: { borderBottom: 'none', paddingTop: 16, paddingBottom: 4 },
        body: { paddingTop: 8 },
        // Give the cancel/save buttons air above and below (lift them off the edge).
        footer: { borderTop: 'none', padding: '16px 24px 20px' },
      }}
      footer={
        <div className="flex items-center justify-end gap-3" dir="rtl">
          <Button onClick={onClose}>ביטול</Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={mutation.isPending}
            disabled={loading}
          >
            שמירה
          </Button>
        </div>
      }
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader size={48} />
          </div>
        ) : (
          <Form<StaffFlat>
            form={form}
            layout="vertical"
            initialValues={DEFAULT_COMPONENT_FLAT as StaffFlat}
            onFinish={onFinish}
            requiredMark={false}
          >
            {/* Payroll inclusion — the only place outside onboarding to re-add
                (or exclude) a staff member. Untracked coaches are skipped in
                every calculation (payroll-report filters WHERE tracked = TRUE). */}
            <div className="flex items-start justify-between gap-4 mb-4 p-3 rounded-lg bg-grey-bg">
              <div>
                <div className="text-label font-medium text-main-text">כלול/י בחישוב השכר</div>
                <div className="text-small text-secondary-text mt-0.5">
                  כשהגדרה זאת לא פעילה, איש הצוות לא ייכלל בדוחות השכר ובחישובים.
                </div>
              </div>
              <Switch
                checked={profile.tracked}
                onChange={(checked) => setProfile((p) => ({ ...p, tracked: checked }))}
              />
            </div>

            {/* ── שכר גלובלי — fixed amount per period, replaces every
                mechanism. Turning it on also turns "כלול בחישוב השכר" on: the
                whole point is to get this person into the report. ────────── */}
            <div className="mb-4 p-3 rounded-lg bg-grey-bg">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-label font-medium text-main-text">שכר גלובלי</div>
                  <div className="text-small text-secondary-text mt-0.5">
                    סכום קבוע בכל תקופת שכר, ללא קשר לשיעורים, לחוגים או לשעון הנוכחות. מתאים לעובדים
                    שאינם מאמנים, למשל עובדי שיווק או משרד.
                  </div>
                </div>
                <Switch
                  aria-label="שכר גלובלי"
                  checked={globalOn}
                  onChange={(checked) => {
                    setGlobalSalary((g) => ({ ...g, enabled: checked }));
                    if (checked) setProfile((p) => ({ ...p, tracked: true }));
                  }}
                />
              </div>
              {globalOn && (
                <div className="mt-3">
                  <div className="text-small text-main-text mb-1">סכום חודשי (₪, לפני מע״מ)</div>
                  <InputNumber
                    aria-label="סכום שכר גלובלי"
                    min={0}
                    step={100}
                    style={{ width: 200 }}
                    value={globalSalary.amount || null}
                    onChange={(v) => setGlobalSalary((g) => ({ ...g, amount: Number(v) || 0 }))}
                    addonAfter="₪"
                  />
                  <div className="text-small text-secondary-text mt-2">
                    כשהשכר הגלובלי פעיל, זה כל השכר של איש הצוות. מנגנוני התשלום ורכיבי השכר האחרים לא
                    יחושבו עבורו. בונוס חד-פעמי, הפחתה, מע״מ וניכוי במקור עדיין יחולו.
                  </div>
                </div>
              )}
            </div>

            {/* ── Pay mechanisms this staff member is assigned to — a subset of
                the box's enabled mechanisms. More than one is additive: e.g.
                hugim + time_clock consolidates into one salary line. ────── */}
            {!globalOn && (
            <div className="mb-4 p-3 rounded-lg bg-grey-bg">
              <div className="text-label font-medium text-main-text mb-2">מנגנוני תשלום</div>
              {staffMechanisms.length === 0 && (
                <div className="text-small text-secondary-text mb-2">
                  לא משויך לאף מנגנון — איש הצוות לא ייכלל בחישוב עד שייבחר מנגנון אחד לפחות.
                </div>
              )}
              <div className="flex flex-col gap-2">
                {boxMechanisms.map((m) => (
                  <label key={m} className="flex items-center gap-2.5 cursor-pointer">
                    <Checkbox
                      checked={staffMechanisms.includes(m)}
                      onChange={(e) =>
                        setStaffMechanisms((prev) =>
                          e.target.checked ? [...prev, m] : prev.filter((x) => x !== m),
                        )
                      }
                    />
                    <span className="text-small text-main-text">{MECHANISM_LABELS[m]}</span>
                  </label>
                ))}
              </div>
            </div>
            )}

            {/* ── P&L unit — ONE shared control for hugim + time_clock, never
                repeated per mechanism. Only shown when the box actually has
                P&L units configured AND this staff member has no
                group_classes assignment (see needsManualUnit: a coach's unit
                is auto-derived from where they taught, which wins over any
                manual value — showing the picker there would be a no-op
                control). ──────────────────────────────────────────────── */}
            {needsManualUnit &&
              pnlUnitsQuery.data != null &&
              pnlUnitsQuery.data.config.enabled &&
              pnlUnitsQuery.data.config.units.length > 0 && (
                <div className="mb-4 p-3 rounded-lg bg-grey-bg">
                  <div className="text-label font-medium text-main-text mb-2">יחידת רווח והפסד</div>
                  <div className="text-small text-secondary-text mb-2">
                    לאיש צוות זה אין מיקום שיעורים שממנו ניתן לשייך יחידה אוטומטית — יש לבחור ידנית,
                    או להשאיר ברירת מחדל.
                  </div>
                  <Select
                    style={{ width: 240 }}
                    value={pnlUnitId ?? PNL_UNIT_NONE}
                    onChange={(v) => setPnlUnitId(v === PNL_UNIT_NONE ? null : v)}
                    options={[
                      {
                        value: PNL_UNIT_NONE,
                        label: `ברירת מחדל של העסק (${
                          pnlUnitsQuery.data.config.units.find(
                            (u) => u.id === defaultUnitId(pnlUnitsQuery.data!.config),
                          )?.name ?? ''
                        })`,
                      },
                      ...pnlUnitsQuery.data.config.units.map((u) => ({ value: u.id, label: u.name })),
                    ]}
                  />
                </div>
              )}

            <SettingsAccordion>
            {!globalOn && (
            <>
            {hasHugim && (
              /* ── Hugim: per-coach percentage ───────────────────────── */
              <>
                <SectionLabel>אחוז מהתשלומים לחוגים</SectionLabel>
                <RolePercentFields
                  value={hugimPercent}
                  onChange={setHugimPercent}
                  helper={(role) => `אחוז ספציפי לאיש צוות זה ${role} בכל החוגים (גובר על אחוז החוג וברירת המחדל). השאר ריק כדי להשתמש בברירת המחדל`}
                  defaults={{
                    primary: hugimQuery.data?.default_percent ?? null,
                    secondary: hugimQuery.data ? (hugimQuery.data.secondary_default_percent ?? 0) : null,
                  }}
                />
                <CoachHugPercentFields
                  value={coachHugPercents}
                  onChange={setCoachHugPercents}
                  staffName={staffName}
                />

                {/* ── מוצרים נלווים: the same two levels, for add-ons ── */}
                <SectionLabel>מוצרים נלווים</SectionLabel>
                <RolePercentFields
                  value={addonsPercent}
                  onChange={setAddonsPercent}
                  helper={(role) => `אחוז ספציפי לאיש צוות זה ${role} ממוצרים נלווים בכל החוגים (גובר על אחוז החוג וברירת המחדל). השאר ריק כדי להשתמש בברירת המחדל`}
                  defaults={{
                    primary: addonsQuery.data?.default_percent ?? null,
                    secondary: addonsQuery.data?.secondary_default_percent ?? null,
                  }}
                />
                <CoachHugPercentFields
                  value={coachAddonPercents}
                  onChange={setCoachAddonPercents}
                  staffName={staffName}
                />

                {/* ── תוספות וקיזוזים ידניים — saved row by row, not with the
                    drawer's save button. ── */}
                {staffId != null && (
                  <ManualAdjustmentsFields
                    staffId={staffId}
                    isSelf={staffId === authUserId}
                    tracked={profile.tracked}
                  />
                )}
              </>
            )}
            {hasGenericPay && (
              <>
                {/* ── Base salary — shared between שיעורים קבוצתיים and שעון
                    נוכחות (same setting, never duplicated when both apply to
                    this staff member). ─────────────────────────────────── */}
                <SettingCard id="base" title="שכר בסיס" summary={srcLabel(baseSource)}>
                  <Form.Item name="base_source" className="mb-0">
                    <Select options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]} />
                  </Form.Item>
                  {baseSource === 'custom' && (
                    <div className="mt-3">
                      <BaseFields form={form} allowHourly={hasTimeClock} />
                      {hasGroupClasses && hasTimeClock && (
                        <div className="text-small text-secondary-text mt-2">
                          שכר לפי שעה מבוסס אך ורק על שעות שעון הנוכחות — שעות הוראה משולמות בנפרד
                          תחת "שכר שיעורים".
                        </div>
                      )}
                    </div>
                  )}
                </SettingCard>

                {/* ── Weekend bonus (class-attendance-based — group_classes only) ── */}
                {hasGroupClasses && (
                <SettingCard id="weekend" title="תוספת סוף שבוע" summary={srcLabel(weekendSource)}>
                  <Form.Item name="weekend_source" className="mb-0">
                    <Select options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]} />
                  </Form.Item>
                  {weekendSource === 'custom' && (
                    <div className="mt-3">
                      <WeekendFields form={form} showEnabled={false} />
                    </div>
                  )}
                </SettingCard>
                )}

                {/* ── Travel — same sharing as base salary above. ────── */}
                <SettingCard id="travel" title="נסיעות" summary={srcLabel(travelSource)}>
                  <Form.Item name="travel_source" className="mb-0">
                    <Select options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]} />
                  </Form.Item>
                  {hasGroupClasses && hasTimeClock && (
                    <div className="text-small text-secondary-text mt-2">
                      ימי העבודה לחישוב הנסיעות כוללים גם ימי שיעורים וגם ימי נוכחות (איחוד של שני
                      המקורות — אותו יום נספר פעם אחת בלבד).
                    </div>
                  )}
                  {travelSource === 'custom' && (
                    <div className="mt-3">
                      <TravelFields form={form} />
                    </div>
                  )}
                  {showKmLocations && (
                    <div className="mt-3">
                      <div className="text-small text-secondary-text mb-2">
                        מרחקים לפי סניף (ההגדרה היא לכיוון אחד; המערכת מכפילה להלוך ושוב לפי ימי
                        העבודה בכל סניף)
                      </div>
                      <TravelKmLocationsFields
                        value={travelKmLocations}
                        onChange={setTravelKmLocations}
                      />
                    </div>
                  )}
                </SettingCard>

                {/* ── The rest are class-attendance-based — group_classes only ── */}
                {hasGroupClasses && (
                <>
                {/* ── Empty-class pay ──────────────────────────────── */}
                <SettingCard
                  id="emptyClassPay"
                  title="תשלום על שיעורים ריקים"
                  summary={srcLabel(emptyClassPaySource)}
                >
                  {/* Always shown: for THIS component "ללא" means the coach is
                      paid the normal class salary for an empty class, not that
                      an empty class pays nothing — worth saying, since the
                      option label is shared by every component. */}
                  <div className="text-small text-secondary-text mb-2">
                    שיעור שהתקיים אך ללא נוכחות משתתפים (לפי שיטת החישוב של העסק). באפשרות "ללא"
                    מקבל איש הצוות על שיעור כזה את התשלום הרגיל, כמו כל שיעור אחר.
                  </div>
                  <Form.Item name="empty_class_pay_source" className="mb-0">
                    <Select options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]} />
                  </Form.Item>
                  {emptyClassPaySource === 'custom' && (
                    <div className="mt-3">
                      <EmptyClassPayFields form={form} showEnabled={false} />
                    </div>
                  )}
                </SettingCard>

                {/* ── Volume bonus (בונוס לפי כמות שיעורים חודשית) ──── */}
                <SettingCard
                  id="volumeBonus"
                  title="בונוס לפי כמות שיעורים חודשית"
                  summary={srcLabel(volumeBonusSource)}
                >
                  <Form.Item name="volume_bonus_source" className="mb-0">
                    <Select options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]} />
                  </Form.Item>
                  {volumeBonusSource === 'custom' && (
                    <div className="mt-3">
                      <div className="text-small text-secondary-text mb-2">
                        תוספת כספית לכל שיעור, שגדלה לפי כמות השיעורים שאיש הצוות העביר החודש. החישוב
                        הוא לפי מדרגות: כל שיעור מזכה בתעריף של המדרגה שבה הוא נמצא. נספרים שיעורים
                        שהתקיימו ושולמו עליהם — לא שיעורים ריקים ולא שיעורים שבוטלו.
                      </div>
                      <VolumeBonusFields form={form} showEnabled={false} />
                    </div>
                  )}
                </SettingCard>

                {/* ── Daily class-count adjustment (התאמת שכר לפי כמות שיעורים ביום) ──── */}
                <SettingCard
                  id="dailyClassAdjustment"
                  title="התאמת שכר לפי כמות שיעורים ביום"
                  summary={srcLabel(dailyClassAdjustmentSource)}
                >
                  <Form.Item name="daily_class_adjustment_source" className="mb-0">
                    <Select options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]} />
                  </Form.Item>
                  {dailyClassAdjustmentSource === 'custom' && (
                    <div className="mt-3">
                      <div className="text-small text-secondary-text mb-2">
                        תוספת או הורדה של סכום קבוע עבור כל שיעור, לפי מספר השיעורים במהלך אותו יום.
                        רלוונטי במידה והתעריף לשיעור משתנה לפי מספר השיעורים באותו יום. נספרים רק
                        שיעורים שהתקיימו ושולמו עליהם - לא שיעורים ריקים ולא שיעורים שבוטלו.
                      </div>
                      <DailyClassAdjustmentFields form={form} showEnabled={false} />
                    </div>
                  )}
                </SettingCard>

                {/* ── Per-series rate (beats קטגוריה + Arbox) ── */}
                <SettingCard id="seriesRates" title="תעריף לפי שיעור קבוע" summary={srcLabel(seriesRatesSource)}>
                  <Form.Item name="series_rates_source" className="mb-0">
                    <Select options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]} />
                  </Form.Item>
                  {seriesRatesSource === 'custom' && (
                    <div className="mt-3">
                      <div className="text-small text-secondary-text mb-2">
                        הרשימה כאן מחליפה את תעריפי השיעורים הקבועים של העסק לאיש צוות זה במלואה.
                      </div>
                      <Form.Item name="series_rates" className="mb-0">
                        <SeriesRateFields staffId={staffId ?? undefined} />
                      </Form.Item>
                    </div>
                  )}
                </SettingCard>

                {/* ── Per-קטגוריה rate (advanced — replaces Arbox's class pay) ── */}
                <SettingCard
                  id="categoryRates"
                  title="תעריף לפי קטגוריה"
                  summary={srcLabel(categoryRatesSource)}
                >
                  <Form.Item name="category_rates_source" className="mb-0">
                    <Select options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]} />
                  </Form.Item>
                  {categoryRatesSource === 'custom' && (
                    <div className="mt-3">
                      <div className="text-small text-secondary-text mb-2">
                        הרשימה כאן מחליפה את תעריפי הקטגוריות של העסק לאיש צוות זה במלואה.
                      </div>
                      <Form.Item name="category_rates" className="mb-0">
                        <CategoryRateFields />
                      </Form.Item>
                    </div>
                  )}
                </SettingCard>

                {/* ── Per-class bonus (advanced) ───────────────────── */}
                <SettingCard
                  id="classBonus"
                  title="תוספת לפי שיעור (מתקדם)"
                  summary={
                    classBonuses.length ? `${classBonuses.length} תוספות מוגדרות` : 'לא הוגדרו תוספות'
                  }
                >
                  <ClassBonusFields value={classBonuses} onChange={setClassBonuses} />
                </SettingCard>

                {/* ── Conversion bonus ladder override (only if box-enabled) ─ */}
                {defaultsQuery.data?.conversion_bonus.enabled && (
                  <SettingCard id="conversion" title="בונוס המרה מניסיון" summary={srcLabel(conversionSource)}>
                    <Form.Item name="conversion_source" className="mb-0">
                      <Select
                        options={SOURCE_OPTIONS as unknown as { value: string; label: string }[]}
                      />
                    </Form.Item>
                    {conversionSource === 'custom' && (
                      <>
                        <div className="text-small text-secondary-text mt-3">
                          הסכום לכל המרה נקבע לפי כמות ההמרות של איש הצוות בתקופה: כל ההמרות מקבלות את
                          הסכום של המדרגה שאליה הגיע.
                        </div>
                        <ConversionTiersField />
                      </>
                    )}
                    <div className="text-small text-secondary-text mt-3">
                      חלון ההמרה ({defaultsQuery.data.conversion_bonus.window_days} ימים) נקבע בהגדרות
                      הכלליות של העסק.
                    </div>
                  </SettingCard>
                )}
                </>
                )}
              </>
            )}

            </>
            )}
            {/* ── Tax classification + bank details ─────────────────── */}
            <SettingCard
              id="tax"
              title="מיסוי ופרטי תשלום"
              summary={[
                TAX_TYPE_LABELS[profile.tax_type] ?? '',
                profile.withholding_pct > 0
                  ? `ניכוי במקור ${formatWithholdingPct(profile.withholding_pct)}`
                  : '',
              ]
                .filter(Boolean)
                .join(' · ')}
            >
              <StaffTaxBankFields
                value={profile}
                staffName={staffName}
                onChange={(patch) => setProfile((p) => ({ ...p, ...patch }))}
              />
            </SettingCard>
            </SettingsAccordion>
          </Form>
        )}
      </ConfigProvider>
    </Drawer>
  );
}

/** A staff member's own (all-courses) rows, folded into one figure per role. */
function ownRolePercents(own: HugimPercentRow[]): RolePercents {
  const pick = (role: HugRole) =>
    own.find((o) => o.hug_key === ANY_HUG && rowRole(o) === role)?.percent ?? null;
  return { primary: pick('primary'), secondary: pick('secondary') };
}

/**
 * The staff member's own all-courses percentage, one block per role: a
 * sub-heading, the helper, the field. Blank = inherit; the placeholder and
 * the helper show the default it inherits.
 */
function RolePercentFields({
  value,
  onChange,
  helper,
  defaults,
}: {
  value: RolePercents;
  onChange: (next: RolePercents) => void;
  helper: (roleWord: string) => string;
  defaults: RolePercents;
}) {
  const blocks: { role: HugRole; title: string; word: string }[] = [
    { role: 'primary', title: 'איש צוות ראשי', word: 'כראשי' },
    { role: 'secondary', title: 'איש צוות משני', word: 'כמשני' },
  ];
  return (
    <>
      {blocks.map((b) => (
        <div key={b.role} className="mb-3">
          <div className="text-label font-medium text-main-text mb-1">{b.title}</div>
          <div className="text-small text-secondary-text mb-2">
            {helper(b.word)}
            {defaults[b.role] != null ? ` (${defaults[b.role]}%)` : ''}.
          </div>
          <InputNumber
            min={0}
            max={100}
            dir="ltr"
            style={{ width: 160 }}
            placeholder={defaults[b.role] != null ? String(defaults[b.role]) : undefined}
            value={value[b.role] ?? undefined}
            onChange={(v) => onChange({ ...value, [b.role]: v == null ? null : Number(v) })}
          />
        </div>
      ))}
      <PercentSumWarning primary={value.primary} secondary={value.secondary} />
    </>
  );
}

/**
 * Save ONE staff member's two levels of a layered hugim percentage — their own
 * all-courses % (null = inherit → the row is deleted) and their per-course
 * list — for both roles, as a diff against the rows loaded when the drawer
 * opened. Shared by the course % and the מוצרים נלווים %, which are laid out
 * identically.
 */
async function saveCoachPercentLevels(
  staffId: number,
  loadedOverrides: HugimPercentRow[],
  coachPercent: RolePercents,
  perHug: CourseRolePercent[],
  api: {
    upsert: (row: HugimPercentRow) => Promise<unknown>;
    remove: (coach: number, hug: string, role: HugRole) => Promise<unknown>;
  },
): Promise<void> {
  for (const role of ['primary', 'secondary'] as const) {
    const mine = loadedOverrides.filter(
      (o) => o.coach_arbox_users_boxes_id === staffId && rowRole(o) === role,
    );
    const loadedDefault = mine.find((o) => o.hug_key === ANY_HUG);
    const own = coachPercent[role];
    if (own == null) {
      if (loadedDefault) await api.remove(staffId, ANY_HUG, role);
    } else if (loadedDefault?.percent !== own) {
      await api.upsert({ coach_arbox_users_boxes_id: staffId, hug_key: ANY_HUG, percent: own, role });
    }
    const loaded = mine.filter((o) => o.hug_key !== ANY_HUG);
    const wanted = perHug.filter((c) => c[role] != null);
    for (const o of loaded) {
      if (!wanted.some((c) => c.hug_key === o.hug_key)) await api.remove(staffId, o.hug_key, role);
    }
    for (const c of wanted) {
      if (loaded.find((o) => o.hug_key === c.hug_key)?.percent !== c[role]) {
        await api.upsert({ coach_arbox_users_boxes_id: staffId, hug_key: c.hug_key, percent: c[role]!, role });
      }
    }
  }
}
